// ============ Tool Executor: Simulated Real Tool Execution ============
// This module provides a unified interface for tool execution.
// Each tool returns realistic, context-aware responses based on input args.

export interface ToolResult {
  status: 'success' | 'error';
  data?: any;
  error?: { code: string; message: string };
  latency_ms: number;
  source?: 'real_api' | 'simulated';
}

// ---- Real API toggle ----
const REAL_API_KEY = 'decisioncraft_use_real_api';

export function getUseRealApi(): boolean {
  return localStorage.getItem(REAL_API_KEY) === 'true';
}

export function setUseRealApi(enabled: boolean): void {
  localStorage.setItem(REAL_API_KEY, enabled ? 'true' : 'false');
}

// ---- Async execution with real API support ----
export async function executeToolAsync(toolName: string, args: any): Promise<ToolResult> {
  const normalizedName = toolName.toLowerCase().replace(/[^a-z]/g, '');

  // Try real API for search and maps if enabled
  if (getUseRealApi()) {
    try {
      if (normalizedName === 'search' || normalizedName === 'browser') {
        const query = String(args?.query || args?.url || args?.keyword || '');
        if (query) {
          const res = await fetch(`/api/tools/search?query=${encodeURIComponent(query)}`);
          if (res.ok) {
            const json = await res.json();
            if (json.success) {
              return {
                status: 'success',
                data: { ...json.data, source: 'real_api' },
                latency_ms: Date.now() & 0xfff,
                source: 'real_api',
              };
            }
          }
        }
      }

      if (normalizedName === 'maps') {
        const origin = args?.origin || args?.from || '上海';
        const destination = args?.destination || args?.to || args?.dest || '杭州';
        const mode = args?.mode || args?.travel_mode || 'driving';
        const res = await fetch(`/api/tools/maps?origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}&mode=${encodeURIComponent(mode)}`);
        if (res.ok) {
          const json = await res.json();
          if (json.success) {
            return {
              status: 'success',
              data: { ...json.data, source: 'real_api' },
              latency_ms: Date.now() & 0xfff,
              source: 'real_api',
            };
          }
        }
      }
    } catch {
      // Network error — fall back to simulated data
    }
  }

  // Fall back to simulated data
  return executeTool(toolName, args);
}

// ---- Simulated data pools ----
const HOTEL_DATA = [
  { name: '西湖国宾馆', price: 880, distance: 0.5, rating: 4.8, amenities: ['泳池', '餐厅', '停车场'] },
  { name: '杭州黄龙饭店', price: 520, distance: 2.1, rating: 4.5, amenities: ['餐厅', '停车场', '健身房'] },
  { name: '如家快捷酒店(西湖店)', price: 280, distance: 1.8, rating: 4.2, amenities: ['停车场'] },
  { name: '全季酒店(武林广场店)', price: 350, distance: 3.5, rating: 4.3, amenities: ['餐厅', '停车场'] },
  { name: '亚朵酒店(滨江店)', price: 420, distance: 5.2, rating: 4.6, amenities: ['餐厅', '健身房', '停车场'] },
];

const TRAIN_DATA = [
  { trainNo: 'G7341', from: '上海虹桥', to: '杭州东', depart: '07:00', arrive: '08:15', duration: '1h15m', price: 73 },
  { trainNo: 'G7343', from: '上海虹桥', to: '杭州东', depart: '09:00', arrive: '10:15', duration: '1h15m', price: 73 },
  { trainNo: 'G7345', from: '上海虹桥', to: '杭州东', depart: '12:00', arrive: '13:20', duration: '1h20m', price: 73 },
  { trainNo: 'G7347', from: '上海虹桥', to: '杭州东', depart: '15:00', arrive: '16:15', duration: '1h15m', price: 73 },
  { trainNo: 'D5571', from: '上海南', to: '杭州', depart: '08:30', arrive: '10:30', duration: '2h00m', price: 55 },
];

const ATTRACTION_DATA = [
  { name: '西湖', ticket: 0, duration: '3-4h', type: '自然风光', family_friendly: true },
  { name: '灵隐寺', ticket: 30, duration: '2h', type: '历史文化', family_friendly: true },
  { name: '宋城', ticket: 320, duration: '4-5h', type: '主题乐园', family_friendly: true },
  { name: '西溪湿地', ticket: 80, duration: '3h', type: '自然风光', family_friendly: true },
  { name: '河坊街', ticket: 0, duration: '2h', type: '美食购物', family_friendly: true },
  { name: '雷峰塔', ticket: 40, duration: '1h', type: '历史文化', family_friendly: true },
  { name: '千岛湖', ticket: 150, duration: '6h', type: '自然风光', family_friendly: true },
];

const WEATHER_DATA = {
  sunny: { condition: '晴', temp: 28, humidity: 55, suggestion: '适合户外活动' },
  cloudy: { condition: '多云', temp: 25, humidity: 65, suggestion: '适宜出行' },
  rainy: { condition: '小雨', temp: 22, humidity: 80, suggestion: '建议携带雨具，优先室内活动' },
  storm: { condition: '暴雨', temp: 20, humidity: 90, suggestion: '不建议户外活动，需调整行程' },
};

const RESTAURANT_DATA = [
  { name: '楼外楼', cuisine: '杭帮菜', price_per_person: 150, rating: 4.5, signature: '西湖醋鱼、龙井虾仁' },
  { name: '知味观', cuisine: '杭帮菜', price_per_person: 80, rating: 4.3, signature: '小笼包、猫耳朵' },
  { name: '外婆家', cuisine: '杭帮菜', price_per_person: 50, rating: 4.4, signature: '茶香鸡、麻婆豆腐' },
  { name: '绿茶餐厅', cuisine: '创意菜', price_per_person: 60, rating: 4.5, signature: '绿茶烤鸡、面包诱惑' },
];

// ---- Market/Business data for non-tourism tasks ----
const MARKET_DATA = [
  { market: '新能源汽车', growth_rate: '35.2%', key_players: ['比亚迪', '特斯拉', '蔚来', '小鹏'], tian: 'high', difficulty: 'high' },
  { market: '跨境电商', growth_rate: '18.5%', key_players: ['SHEIN', 'Temu', '阿里国际站', '京东全球购'], tian: 'medium', difficulty: 'medium' },
  { market: 'SaaS企业服务', growth_rate: '22.8%', key_players: ['钉钉', '飞书', '企业微信', 'Salesforce'], tian: 'high', difficulty: 'high' },
];

// ---- Main execution function ----
export function executeTool(toolName: string, args: any): ToolResult {
  const start = Date.now();
  const normalizedName = toolName.toLowerCase().replace(/[^a-z]/g, '');

  try {
    let data: any;

    switch (normalizedName) {
      case 'search':
      case 'browser':
        data = executeSearch(args);
        break;
      case 'maps':
        data = executeMaps(args);
        break;
      case 'calculator':
        data = executeCalculator(args);
        break;
      case 'spreadsheet':
        data = executeSpreadsheet(args);
        break;
      default:
        // Try to match by tool name patterns
        if (normalizedName.includes('search') || normalizedName.includes('browser')) {
          data = executeSearch(args);
        } else if (normalizedName.includes('map')) {
          data = executeMaps(args);
        } else if (normalizedName.includes('calc')) {
          data = executeCalculator(args);
        } else if (normalizedName.includes('sheet') || normalizedName.includes('spread')) {
          data = executeSpreadsheet(args);
        } else {
          return {
            status: 'error',
            error: { code: 'UNKNOWN_TOOL', message: `Tool "${toolName}" is not recognized` },
            latency_ms: Date.now() - start,
          };
        }
    }

    // Add realistic latency (50-300ms)
    const latency = Math.floor(Math.random() * 250) + 50;
    return { status: 'success', data, latency_ms: latency };
  } catch (err) {
    return {
      status: 'error',
      error: { code: 'EXECUTION_ERROR', message: err instanceof Error ? err.message : 'Unknown error' },
      latency_ms: Date.now() - start,
    };
  }
}

// ---- Tool implementations ----

function executeSearch(args: any): any {
  const query = String(args?.query || args?.url || args?.keyword || '').toLowerCase();

  // Hotel search
  if (query.includes('hotel') || query.includes('酒店') || query.includes('住宿')) {
    return {
      type: 'hotel_results',
      results: HOTEL_DATA.length,
      hotels: HOTEL_DATA,
      tip: '建议根据预算和位置选择，西湖附近酒店价格较高但位置好',
    };
  }

  // Train/rail search
  if (query.includes('train') || query.includes('高铁') || query.includes('火车') || query.includes('交通')) {
    return {
      type: 'train_results',
      results: TRAIN_DATA.length,
      trains: TRAIN_DATA,
      tip: '建议提前购票，G字头高铁更快但价格略高',
    };
  }

  // Attraction search
  if (query.includes('attraction') || query.includes('景点') || query.includes('西湖') || query.includes('游玩')) {
    return {
      type: 'attraction_results',
      results: ATTRACTION_DATA.length,
      attractions: ATTRACTION_DATA,
      tip: '西湖免费，建议早上游览避开人流',
    };
  }

  // Weather search
  if (query.includes('weather') || query.includes('天气') || query.includes('气候')) {
    const conditions = Object.keys(WEATHER_DATA) as (keyof typeof WEATHER_DATA)[];
    const condition = conditions[Math.floor(Math.random() * conditions.length)];
    return {
      type: 'weather',
      location: '杭州',
      forecast: WEATHER_DATA[condition],
      tip: '建议根据天气调整行程顺序',
    };
  }

  // Restaurant search
  if (query.includes('restaurant') || query.includes('餐厅') || query.includes('美食') || query.includes('吃')) {
    return {
      type: 'restaurant_results',
      results: RESTAURANT_DATA.length,
      restaurants: RESTAURANT_DATA,
      tip: '楼外楼是杭州老字号，适合体验杭帮菜',
    };
  }

  // Market/business search
  if (query.includes('market') || query.includes('市场') || query.includes('行业') || query.includes('竞争')) {
    return {
      type: 'market_analysis',
      markets: MARKET_DATA,
      tip: '建议结合SWOT框架分析市场进入策略',
    };
  }

  // Budget/finance search
  if (query.includes('budget') || query.includes('预算') || query.includes('财务') || query.includes('投资')) {
    return {
      type: 'finance_info',
      rates: { savings: '2.5%', bond: '3.8%', stock_index: '8.2%', reit: '5.5%' },
      tip: '建议根据风险承受能力配置资产组合',
    };
  }

  // Generic search
  return {
    type: 'generic_results',
    query: query,
    results: 5,
    snippets: [
      { title: `${query} - 相关信息`, snippet: `关于"${query}"的搜索结果，包含多个相关信息来源。` },
      { title: `${query} - 详细分析`, snippet: `对"${query}"的深度分析，涵盖关键要素和注意事项。` },
    ],
  };
}

function executeMaps(args: any): any {
  const origin = String(args?.origin || args?.from || '上海').toLowerCase();
  const destination = String(args?.destination || args?.to || args?.dest || '杭州').toLowerCase();
  const mode = String(args?.mode || args?.travel_mode || 'driving').toLowerCase();

  const distances: Record<string, number> = {
    '上海-杭州': 170, 'shanghai-hangzhou': 170,
    '杭州-上海': 170, 'hangzhou-shanghai': 170,
  };

  const key = `${origin}-${destination}`;
  const distance = distances[key] ?? Math.floor(Math.random() * 200) + 50;

  let duration: string;
  let price: number;

  switch (mode) {
    case 'driving':
      duration = `${Math.floor(distance / 80 * 60)}分钟`;
      price = Math.floor(distance * 0.8); // toll + gas
      break;
    case 'transit':
    case 'rail':
    case 'train':
      duration = `${Math.floor(distance / 200 * 60)}分钟`;
      price = Math.floor(distance * 0.45);
      break;
    case 'walking':
      duration = `${Math.floor(distance * 12)}分钟`;
      price = 0;
      break;
    default:
      duration = `${Math.floor(distance / 80 * 60)}分钟`;
      price = Math.floor(distance * 0.8);
  }

  return {
    origin: args?.origin || '上海',
    destination: args?.destination || '杭州',
    mode,
    distance_km: distance,
    duration,
    estimated_cost: price,
    route_summary: `从${args?.origin || '上海'}到${args?.destination || '杭州'}，${mode === 'driving' ? '驾车' : mode === 'transit' ? '高铁' : '步行'}约${duration}`,
  };
}

function executeCalculator(args: any): any {
  const expr = String(args?.expression || args?.formula || args?.equation || '');

  // Try to evaluate simple math expressions
  try {
    // Handle budget calculations
    if (expr.includes('hotel') || expr.includes('transport') || expr.includes('food') || expr.includes('attraction')) {
      const hotel = HOTEL_DATA[1].price; // mid-range
      const transport = TRAIN_DATA[0].price * 2; // round trip
      const food = 150; // per day
      const attraction = 50;
      const total = hotel + transport + food + attraction;
      return {
        expression: expr,
        result: total,
        breakdown: { hotel, transport, food, attraction, total },
        tip: '预算分配建议：住宿40%、交通20%、餐饮25%、景点15%',
      };
    }

    // Handle ROI calculations
    if (expr.includes('roi') || expr.includes('return') || expr.includes('investment')) {
      const revenue = 5000000;
      const cost = 3000000;
      const roi = ((revenue - cost) / cost * 100).toFixed(1);
      return {
        expression: expr,
        result: { revenue, cost, roi: `${roi}%`, net_profit: revenue - cost },
        tip: 'ROI计算：(收入-成本)/成本×100%',
      };
    }

    // Simple arithmetic
    const cleaned = expr.replace(/[^0-9+\-*/().,\s]/g, '');
    if (cleaned.trim()) {
      // eslint-disable-next-line no-new-func
      const result = Function(`"use strict"; return (${cleaned})`)();
      return {
        expression: expr,
        result: typeof result === 'number' ? Math.round(result * 100) / 100 : result,
      };
    }

    return {
      expression: expr,
      result: '无法解析表达式',
      tip: '支持加减乘除、预算计算、ROI计算等',
    };
  } catch {
    return {
      expression: expr,
      result: '计算错误',
      error: '表达式格式不正确',
    };
  }
}

function executeSpreadsheet(args: any): any {
  const action = String(args?.action || args?.type || 'analyze').toLowerCase();

  switch (action) {
    case 'compare':
      return {
        action: 'compare',
        comparison: {
          options: 3,
          criteria: ['价格', '位置', '评分', '设施'],
          best_value: HOTEL_DATA[2].name,
          best_rated: HOTEL_DATA[0].name,
          summary: '如家快捷酒店性价比最高，西湖国宾馆评分最高',
        },
      };

    case 'analyze':
      return {
        action: 'analyze',
        analysis: {
          total_items: HOTEL_DATA.length + TRAIN_DATA.length,
          avg_hotel_price: Math.round(HOTEL_DATA.reduce((s, h) => s + h.price, 0) / HOTEL_DATA.length),
          avg_train_price: TRAIN_DATA[0].price,
          recommendation: '建议选择中档酒店+高铁出行，平衡舒适度和成本',
        },
      };

    case 'create':
      return {
        action: 'create',
        spreadsheet_id: `ss-${Date.now()}`,
        rows: 10,
        columns: 5,
        status: 'created',
      };

    default:
      return {
        action,
        status: 'completed',
        data: args?.data ?? [],
      };
  }
}

// ---- Tool metadata for display ----
export const TOOL_DESCRIPTIONS: Record<string, { input_desc: string; output_desc: string }> = {
  search: {
    input_desc: 'query: 搜索关键词',
    output_desc: '返回搜索结果列表，包含标题、摘要和相关数据',
  },
  browser: {
    input_desc: 'url: 要访问的网页地址',
    output_desc: '返回网页内容和提取的结构化信息',
  },
  maps: {
    input_desc: 'origin, destination, mode: 起点、终点、出行方式',
    output_desc: '返回距离、时长、费用和路线摘要',
  },
  calculator: {
    input_desc: 'expression: 数学表达式或预算公式',
    output_desc: '返回计算结果和分项明细',
  },
  spreadsheet: {
    input_desc: 'action, data: 操作类型(compare/analyze/create)和数据',
    output_desc: '返回对比分析结果或创建的表格信息',
  },
};
